// Priority 2 — Dev thread UI: tracked claude sessions inside Hub People.
//
// Architect redline #1: status colour is DERIVED. Backend returns
// `status_color` on every session response so the frontend never computes
// it from a stored value. We mirror that mapping here for cases where we
// only have a status string (e.g. inline status logs).

import { state } from '../state';
import { get, post } from '../api';
import { showToast } from '../components/toast';
import { promptModal } from '../components/modal';
import { parseIsoUtc } from '../util';

export type DevSessionStatus =
  | 'queued' | 'running' | 'reviewing' | 'done' | 'error' | 'cancelled' | 'idle';

export type DevSessionApprovalStatus = 'pending' | 'approved' | 'rejected' | 'none';

export type DevStatusColor = 'amber' | 'green' | 'red' | 'yellow' | 'grey';

export interface DevSessionMetric {
  id: string;
  session_id: string;
  polled_at: string;
  context_used_tokens: number;
  context_max_tokens: number;
  context_pct: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd_running: number;
  files_touched: number;
  commits_count: number;
}

export interface DevSession {
  id: string;
  name: string;
  brief_path: string | null;
  agent_target: string;
  agent_model: string;
  channel_id: string;
  thread_id: string | null;
  parent_session_id: string | null;
  session_uuid: string | null;
  worktree_path: string | null;
  started_at: string;
  ended_at: string | null;
  status: DevSessionStatus;
  approval_status: DevSessionApprovalStatus;
  approved_by: string | null;
  approved_at: string | null;
  exit_summary: string | null;
  // 2B runtime columns (migration 026). Backend omits them only for rows
  // predating 026, but SQLite ALTER ADD COLUMN gave every row defaults.
  worker_container: string | null;
  archive_status: string | null;
  archived_at: string | null;
  mcp_writeback_at: string | null;
  archive_error: string | null;
  stream_path: string | null;
  permission_mode: string;
  status_color: DevStatusColor;
  latest_metric: DevSessionMetric | null;
}

export interface DevSessionLog {
  id: string;
  session_id: string;
  kind:
    | 'start' | 'progress' | 'end' | 'error'
    | 'review_request' | 'review_pass' | 'review_fail'
    | 'approve' | 'reject';
  message: string;
  metadata_json: string;
  created_at: string;
}

export interface ThreadStatusView {
  thread_id: string;
  session_id: string | null;
  status: DevSessionStatus;
  last_update_at: string;
  status_color: DevStatusColor;
}

export interface ClaudeUsageWindow {
  period_start: string;
  period_end: string;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  requests: number;
}

export interface ClaudeUsageSummary {
  today: ClaudeUsageWindow;
  last_5h: ClaudeUsageWindow;
  month: ClaudeUsageWindow;
  generated_at: string;
}

/** Mirror of backend `status_color()`. Only used when the backend hasn't
 *  attached `status_color` (e.g. raw thread_status string from polling). */
export function statusToColor(status: string): DevStatusColor {
  switch (status) {
    case 'running':   return 'amber';
    case 'done':      return 'green';
    case 'error':     return 'red';
    case 'reviewing': return 'yellow';
    case 'cancelled': return 'grey';
    default:          return 'grey';
  }
}

export async function listDevSessions(opts?: {
  status?: string;
  channel_id?: string;
  limit?: number;
  archived?: boolean;
  cursor?: string | null;
}): Promise<DevSession[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.channel_id) params.set('channel_id', opts.channel_id);
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.archived) params.set('archived', 'true');
  if (opts?.cursor) params.set('cursor', opts.cursor);
  const qs = params.toString();
  return get<DevSession[]>(`/api/dev-sessions${qs ? `?${qs}` : ''}`);
}

export async function unArchiveDevSession(id: string): Promise<{ ok: boolean; session_id: string }> {
  const personId = state.ownerId || state.people[0]?.id || '';
  return post<{ ok: boolean; session_id: string }>(
    `/api/dev-sessions/${encodeURIComponent(id)}/un-archive`,
    { person_id: personId },
  );
}

export async function retryMcpWriteback(id: string): Promise<{ ok: boolean; session_id: string }> {
  const personId = state.ownerId || state.people[0]?.id || '';
  return post<{ ok: boolean; session_id: string }>(
    `/api/dev-sessions/${encodeURIComponent(id)}/retry-mcp-writeback`,
    { person_id: personId },
  );
}

export async function getDevSession(id: string): Promise<DevSession> {
  return get<DevSession>(`/api/dev-sessions/${encodeURIComponent(id)}`);
}

export async function getDevSessionLog(id: string): Promise<DevSessionLog[]> {
  return get<DevSessionLog[]>(`/api/dev-sessions/${encodeURIComponent(id)}/log`);
}

export async function approveDevSession(id: string): Promise<DevSession> {
  const personId = state.ownerId || state.people[0]?.id || '';
  return post<DevSession>(`/api/dev-sessions/${encodeURIComponent(id)}/approve`, { person_id: personId });
}

export async function rejectDevSession(id: string, reason: string): Promise<DevSession> {
  const personId = state.ownerId || state.people[0]?.id || '';
  return post<DevSession>(`/api/dev-sessions/${encodeURIComponent(id)}/reject`, { person_id: personId, reason });
}

export async function fetchThreadStatusBatch(threadIds: string[]): Promise<Map<string, ThreadStatusView>> {
  const map = new Map<string, ThreadStatusView>();
  if (threadIds.length === 0) return map;
  try {
    const ids = threadIds.join(',');
    const rows = await get<ThreadStatusView[]>(`/api/thread-status?ids=${encodeURIComponent(ids)}`);
    for (const r of rows) map.set(r.thread_id, r);
  } catch { /* silent — dots just won't render */ }
  return map;
}

export async function fetchClaudeUsage(): Promise<ClaudeUsageSummary | null> {
  try {
    return await get<ClaudeUsageSummary>('/api/usage/claude');
  } catch { return null; }
}

// ── status card rendering (used inside thread panel + dashboard) ───────────

export function statusCardHtml(log: DevSessionLog): string {
  const colorByKind: Record<string, DevStatusColor> = {
    start: 'grey',
    progress: 'grey',
    end: 'green',
    error: 'red',
    review_request: 'yellow',
    review_pass: 'green',
    review_fail: 'red',
    approve: 'green',
    reject: 'red',
  };
  const labelByKind: Record<string, string> = {
    start: '▶ Starting',
    progress: '· Progress',
    end: '✓ Done',
    error: '✗ Error',
    review_request: '? Awaiting review',
    review_pass: '✓ Review passed',
    review_fail: '✗ Review failed',
    approve: '✓ Approved',
    reject: '✗ Rejected',
  };
  if (log.kind === 'progress') {
    return `<div class="ds-log-progress"><span class="ds-log-time">${escTime(log.created_at)}</span> ${escHtml(log.message || '…')}</div>`;
  }
  const color = colorByKind[log.kind] ?? 'grey';
  const label = labelByKind[log.kind] ?? log.kind;
  return `<div class="ds-card ds-card-${color}">
    <div class="ds-card-head">
      <span class="ds-card-label">${escHtml(label)}</span>
      <span class="ds-card-time">${escTime(log.created_at)}</span>
    </div>
    ${log.message ? `<div class="ds-card-body">${escHtml(log.message)}</div>` : ''}
  </div>`;
}

export function metricsLineHtml(m: DevSessionMetric | null | undefined): string {
  if (!m) return '';
  const pct = Math.round(m.context_pct);
  const tin = formatTokens(m.tokens_in);
  const tout = formatTokens(m.tokens_out);
  const cost = `$${m.cost_usd_running.toFixed(2)}`;
  return `<div class="ds-metrics">
    <span title="Context window used"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-2px"><path d="M12 2a9 9 0 0 0-9 9c0 4.17 2.84 7.67 6.69 8.69a2 2 0 0 0 4.62 0C18.16 18.67 21 15.17 21 11a9 9 0 0 0-9-9z"/><circle cx="9" cy="10" r="1"/><circle cx="15" cy="10" r="1"/></svg> ${pct}%</span>
    <span>·</span>
    <span title="Tokens">${tin} in / ${tout} out</span>
    <span>·</span>
    <span title="Spend">${cost}</span>
    <span>·</span>
    <span title="Files touched">${m.files_touched} files</span>
    <span>·</span>
    <span title="Commits">${m.commits_count} commits</span>
  </div>`;
}

export function approveRejectButtonsHtml(session: DevSession, isAdmin: boolean): string {
  if (!isAdmin) return '';
  if (session.approval_status !== 'pending') return '';
  if (session.status !== 'done' && session.status !== 'reviewing' && session.status !== 'error') return '';
  return `<div class="ds-approve-bar">
    <button class="ds-approve-btn ds-approve-yes" data-action="approve" data-session="${escAttr(session.id)}">✓ Approve</button>
    <button class="ds-approve-btn ds-approve-no" data-action="reject" data-session="${escAttr(session.id)}">✗ Reject</button>
  </div>`;
}

export function bindApproveRejectActions(root: HTMLElement, onChange: (s: DevSession) => void): void {
  root.querySelectorAll<HTMLButtonElement>('.ds-approve-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const sid = btn.dataset['session'];
      const action = btn.dataset['action'];
      if (!sid || !action) return;
      btn.disabled = true;
      try {
        if (action === 'approve') {
          const updated = await approveDevSession(sid);
          showToast('Approved', 'ok');
          onChange(updated);
        } else if (action === 'reject') {
          promptModal(
            'Reject session',
            'Reason for rejection:',
            (reason) => {
              if (!reason.trim()) { btn.disabled = false; return; }
              void rejectDevSession(sid, reason.trim())
                .then((updated) => { showToast('Rejected', 'ok'); onChange(updated); })
                .catch((err: unknown) => { showToast(`reject failed: ${String(err)}`, 'err'); btn.disabled = false; });
            },
            { placeholder: 'Reason…', confirmLabel: 'Reject', required: true }
          );
          // Cancel: re-enable button on close — handled by checking on next click.
          btn.disabled = false;
        }
      } catch (err) {
        showToast(`${action} failed: ${err}`, 'err');
        btn.disabled = false;
      }
    });
  });
}

// ── tiny helpers ───────────────────────────────────────────────────────────

function escHtml(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function escTime(iso: string): string {
  const d = parseIsoUtc(iso);
  if (!d) return escHtml(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString()} ${time}`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function timeAgoShort(iso: string): string {
  const d = parseIsoUtc(iso)?.getTime() ?? NaN;
  if (!Number.isFinite(d)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - d) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function isCurrentUserServerOwner(): boolean {
  const oid = state.ownerId;
  if (!oid) return false;
  return state.servers.some((s) => s.owner_id === oid);
}

// ── reusable dashboard widgets (mounted from any home/landing surface) ─────

const widgetTimers = new WeakMap<HTMLElement, ReturnType<typeof setInterval>>();

/** Render the Active Dev Sessions widget into `container` and start polling.
 *  Calling renderDevSessionsWidget(container) again replaces the previous
 *  timer for that container. */
export function renderDevSessionsWidget(container: HTMLElement): void {
  container.innerHTML = `<div class="ds-widget-loading">Loading dev sessions…</div>`;
  void refreshInto(container);
  const prev = widgetTimers.get(container);
  if (prev) clearInterval(prev);
  const t = setInterval(() => {
    if (!container.isConnected) { clearInterval(t); widgetTimers.delete(container); return; }
    if (document.visibilityState !== 'visible') return;
    void refreshInto(container);
  }, 60_000);
  widgetTimers.set(container, t);
}

async function refreshInto(container: HTMLElement): Promise<void> {
  let sessions: DevSession[] = [];
  try {
    sessions = await listDevSessions({ status: 'running,reviewing,done', limit: 12 });
  } catch {
    container.innerHTML = `<div class="ds-widget-empty">Failed to load dev sessions.</div>`;
    return;
  }
  if (sessions.length === 0) {
    container.innerHTML = `<div class="ds-widget-empty">No active dev sessions. Dispatch one via <code>POST /api/dev-sessions/start</code>.</div>`;
    return;
  }
  const isAdmin = isCurrentUserServerOwner();
  container.innerHTML = `<div class="ds-tile-grid">${sessions.map((s) => devSessionTile(s, isAdmin)).join('')}</div>`;
  bindApproveRejectActions(container, () => { void refreshInto(container); });
}

function devSessionTile(s: DevSession, isAdmin: boolean): string {
  const colorClass = `ds-tile-${s.status_color}`;
  const personName = s.approved_by ? state.people.find((p) => p.id === s.approved_by)?.name ?? '' : '';
  const verdict = s.approval_status === 'approved'
    ? `<span class="ds-verdict ds-verdict-ok">Approved${personName ? ` by ${escHtml(personName)}` : ''}</span>`
    : s.approval_status === 'rejected'
      ? `<span class="ds-verdict ds-verdict-bad">Rejected${personName ? ` by ${escHtml(personName)}` : ''}</span>`
      : '';
  return `<div class="ds-tile ${colorClass}" data-thread="${escAttr(s.thread_id ?? '')}">
    <div class="ds-tile-head">
      <span class="ds-tile-dot ds-dot-${s.status_color}" title="${escAttr(s.status)}"></span>
      <span class="ds-tile-name">${escHtml(s.name)}</span>
      <span class="ds-tile-agent">${escHtml(s.agent_target)}</span>
    </div>
    <div class="ds-tile-meta">
      <span>${escHtml(s.status)}</span>
      <span>·</span>
      <span title="Started">${timeAgoShort(s.started_at)}</span>
      ${verdict ? `<span>·</span>${verdict}` : ''}
    </div>
    ${s.latest_metric ? metricsLineHtml(s.latest_metric) : ''}
    ${approveRejectButtonsHtml(s, isAdmin)}
  </div>`;
}

/** Render the Claude usage widget. Polled at the same cadence; tracks
 *  pipeline-only spend (not personal claude.ai or direct CLI). */
export function renderClaudeUsageWidget(container: HTMLElement): void {
  container.innerHTML = `<div class="cu-widget-loading">Loading usage…</div>`;
  void refreshUsageInto(container);
  const prev = widgetTimers.get(container);
  if (prev) clearInterval(prev);
  const t = setInterval(() => {
    if (!container.isConnected) { clearInterval(t); widgetTimers.delete(container); return; }
    if (document.visibilityState !== 'visible') return;
    void refreshUsageInto(container);
  }, 60_000);
  widgetTimers.set(container, t);
}

async function refreshUsageInto(container: HTMLElement): Promise<void> {
  const summary = await fetchClaudeUsage();
  if (!summary) {
    container.innerHTML = `<div class="cu-widget-empty">Failed to load usage.</div>`;
    return;
  }
  const fmt = (n: number) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n/1_000).toFixed(1)}k` : String(n);
  container.innerHTML = `
    <div class="cu-grid">
      ${cuTile('Today', summary.today, fmt)}
      ${cuTile('Last 5h', summary.last_5h, fmt)}
      ${cuTile('This Month', summary.month, fmt)}
    </div>
    <div class="cu-footnote">Tracks pipeline (bridge-dispatched) usage only. Personal claude.ai + direct CLI not included.</div>
  `;
}

function cuTile(label: string, w: ClaudeUsageWindow, fmt: (n: number) => string): string {
  return `<div class="cu-tile">
    <div class="cu-tile-label">${escHtml(label)}</div>
    <div class="cu-tile-cost">$${w.cost_usd.toFixed(2)}</div>
    <div class="cu-tile-toks">${fmt(w.tokens_in)} in / ${fmt(w.tokens_out)} out</div>
    <div class="cu-tile-reqs">${w.requests} request${w.requests === 1 ? '' : 's'}</div>
  </div>`;
}

/** Stop polling all dev-session widgets in `container` (use on unmount). */
export function cleanupDevSessionWidgets(container: HTMLElement): void {
  const t = widgetTimers.get(container);
  if (t) { clearInterval(t); widgetTimers.delete(container); }
}
